import { useState } from 'react';
import { ChevronDown, ChevronUp, RefreshCw, X } from 'lucide-react';

import { KubesoloHostResult, KubesoloTest } from '@api/types.gen';

import { addPlural } from '@/react/common/string-utils';

import { Icon } from '@@/Icon';
import { LoadingButton } from '@@/buttons';
import { CopyButton } from '@@/buttons/CopyButton';

import { useRetestMutation } from '../queries/useRetestMutation';

import { buildCausePrompt } from './failurePrompt';
import { HostList } from './HostList';
import { FailureCause, groupByCause } from './groupByCause';
import { groupLabels } from './groupHosts';
import { explainFailure } from './stepFailures';
import { stepLabels } from './steps';

interface Props {
  hosts: Array<KubesoloHostResult>;
  test: KubesoloTest;
}

/** Machines that need attention, one line per cause, so a hundred machines failing the same way read as one problem. */
export function FailureGroups({ hosts, test }: Props) {
  const causes = groupByCause(hosts);
  if (causes.length === 0) {
    return <p className="text-muted">No machines.</p>;
  }

  return (
    <ul
      aria-label={groupLabels.attention}
      className="m-0 mb-3 list-none divide-y divide-solid divide-gray-3 p-0 th-highcontrast:divide-white th-dark:divide-gray-9"
    >
      {causes.map((cause) => (
        <CauseItem
          key={cause.key}
          cause={cause}
          test={test}
          defaultExpanded={causes.length === 1}
        />
      ))}
    </ul>
  );
}

function CauseItem({
  cause,
  test,
  defaultExpanded,
}: {
  cause: FailureCause;
  test: KubesoloTest;
  defaultExpanded: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const retestMutation = useRetestMutation();
  const { fix } = explainFailure(cause.step, test);
  const title = `${stepLabels[cause.step.name]}: ${cause.title}`;
  const count = cause.hosts.length;

  return (
    <li className="border-x-0 py-2">
      <button
        type="button"
        className="flex w-full items-center gap-2 border-0 bg-transparent p-0 text-left"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        data-cy="kubesolo-cause-toggle"
      >
        <Icon icon={X} mode="danger" />
        <span className="min-w-0 flex-1 font-medium break-words">{title}</span>
        <span className="text-muted small whitespace-nowrap">
          {addPlural(count, 'machine')}
        </span>
        <Icon icon={isExpanded ? ChevronUp : ChevronDown} />
      </button>

      {isExpanded && (
        <div className="mt-2 flex flex-col gap-2 pl-6">
          {fix && (
            <div className="text-muted">
              <span className="font-semibold">Fix:</span> {fix}
            </div>
          )}
          {cause.unrecognisedError !== undefined && (
            <UnrecognisedError error={cause.unrecognisedError} />
          )}
          <div className="flex gap-2">
            <LoadingButton
              size="small"
              color="default"
              icon={RefreshCw}
              isLoading={retestMutation.isLoading}
              loadingText="Starting..."
              onClick={() =>
                retestMutation.mutate({
                  testId: test.id,
                  hostIds: cause.hosts.map(({ id }) => id),
                })
              }
              data-cy="kubesolo-retest-cause-button"
            >
              {count === 1
                ? 'Re-test this machine'
                : `Re-test these ${count} machines`}
            </LoadingButton>
            <CopyButton
              copyText={buildCausePrompt(test, cause)}
              title="Copy the failures as a prompt for an AI assistant"
              aria-label="Copy as prompt"
              data-cy="kubesolo-copy-prompt-button"
            >
              Copy as prompt
            </CopyButton>
          </div>
          <HostList label={title} hosts={cause.hosts} test={test} hideFix />
        </div>
      )}
    </li>
  );
}

function UnrecognisedError({ error }: { error: string }) {
  return (
    <div className="flex flex-col gap-1">
      <pre className="m-0 break-words whitespace-pre-wrap">{error}</pre>
      <span className="text-muted">
        Portainer doesn&apos;t recognise this error, so it has no fix to
        suggest. Use Copy as prompt to investigate it.
      </span>
    </div>
  );
}
